/**
 * The dialog that registers a tracked account (`Campanha · Adicionar conta`, frame 86:2). Every
 * account collects through one of the organisation's collection profiles, chosen here; the
 * profile is the account's whole collection configuration.
 *
 * The `Conta` field takes a profile URL or a bare handle and derives `platform` from the host
 * (defaulting to `instagram` for a bare handle) -- there is no separate platform control in the
 * frame. `alignment` starts unchosen: guessing it misrecords the account's alignment. Every
 * account this dialog
 * registers is a public profile the campaign already follows, never one whose holder consented
 * through this form, so `basis` is fixed at `DEFAULT_ACCOUNT_BASIS` rather than asked.
 *
 * `open`/`onOpenChange` are optional: uncontrolled by default (this dialog owns its own trigger
 * button, as every story here still opens it through `play`), controlled when the caller passes
 * both -- `FormDialog` already accepts a dialog with no `trigger` at all, so `./routes.ts` opens
 * this one from the accounts list's own "Adicionar conta" button instead of rendering a second,
 * redundant trigger.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { FormDialog } from '../../../chrome/form';
import { fieldControlProps, fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import type { ApiFailure } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { Alert, AlertTitle } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Field, FieldError, FieldLabel, FieldLegend, FieldSet } from '../../../ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import { cataloguePath } from '../../control/paths';
import { AccountUrlField } from './AccountUrlField';
import { aat } from './addAccountMessages';
import { CollectionChips } from './CollectionChips';
import { CostPreview } from './CostPreview';
import { DEFAULT_TAG_ICON, TagGlyph, tagIconName } from '../../tenant/tags/icons';
import { analysisScopeStateSchema, type AnalysisScope } from '../adversaries/api/schemas';
import { useRegisterAccount, useTagCategories } from './api/queries';
import type { Alignment, Basis, Platform, TagCategory, TagPair } from './api/schemas';
import { ReachField } from './ReachField';
import { EMPTY_REACH, type ReachValue } from './ReachField';
import { TagField } from './TagField';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';
interface AlignmentOption {
  /** The alignment category's value id, or the bare key while the vocabulary is unread. */
  id: string;
  key: Alignment;
  label: string;
  icon: string;
  valueId?: string;
}

/** The campaign's alignment values, the fixed ones and any the organisation added, except those
 *  counting as the campaign's own accounts, which this dialog never registers. */
function alignmentOptions(categories: TagCategory[] | undefined): AlignmentOption[] {
  const category = categories?.find((candidate) => candidate.locked);
  if (category === undefined) {
    return (['ALLY', 'NEUTRAL', 'ADVERSARY'] as const).map((key) => ({
      id: key,
      key,
      label: aat(`alignment.${key}`),
      icon: DEFAULT_TAG_ICON,
    }));
  }
  return category.values.flatMap((value) =>
    value.alignment && value.alignment !== 'CAMPAIGN'
      ? [
          {
            id: value.id,
            key: value.alignment,
            label: value.name,
            icon: tagIconName(value, category),
            valueId: value.id,
          },
        ]
      : [],
  );
}
/** Every account this dialog registers is a public profile the campaign already follows -- the
 *  dialog collects no consent, so `CONSENT` is never a truthful basis for it to send. */
export const DEFAULT_ACCOUNT_BASIS: Basis = 'PUBLIC';
/** A recognised host names the platform; a bare handle (no host) defaults to `instagram` -- the
 *  frame carries no separate platform control. */
function parseAccount(raw: string): { platform: Platform; handle: string } | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const withHost = trimmed.match(
    /^(?:https?:\/\/)?(?:www\.)?(instagram|tiktok)\.com\/@?([^/?#]+)/i,
  );
  if (withHost) return { platform: withHost[1]!.toLowerCase() as Platform, handle: withHost[2]! };
  return { platform: 'instagram', handle: trimmed.replace(/^@/, '') };
}
function refusalCopy(failure: ApiFailure): string {
  switch (failure.kind) {
    case 'conflict':
      return aat('error.duplicate');
    case 'not-found':
      return aat('error.profileMissing');
    case 'forbidden':
      return aat('error.forbidden');
    default:
      return aat('error.generic');
  }
}
interface FormState {
  account: string;
  /** An `AlignmentOption.id`. */
  alignment: string | null;
  reach: ReachValue;
  tags: TagPair[];
  profileId: string | null;
  /** Shown, and sent, only for an Adversária pick; `ALL` is the default it starts on
   *  (`tag_values.analysis_scope`'s own default for that alignment key), so submitting it
   *  unchanged writes nothing -- only `RELATED` needs a follow-up write. */
  scope: AnalysisScope;
}
const EMPTY_FORM: FormState = {
  account: '',
  alignment: null,
  reach: EMPTY_REACH,
  tags: [],
  profileId: null,
  scope: 'ALL',
};
export function AddAccount(props: {
  campaignId: string;
  tenantId: string;
  canWrite: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}): ReactNode {
  const client = useApiClient();
  const controlled = props.open !== undefined && props.onOpenChange !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlled ? props.open! : uncontrolledOpen;
  const setOpen = controlled ? props.onOpenChange! : setUncontrolledOpen;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [accountMissing, setAccountMissing] = useState(false);
  const [alignmentMissing, setAlignmentMissing] = useState(false);
  const vocabulary = useTagCategories(props.campaignId, open);
  const alignments = alignmentOptions(vocabulary.data);
  const [profileMissing, setProfileMissing] = useState(false);
  const accountId = useId();
  const profileId = useId();
  const profiles = useQuery({
    queryKey: ['campaign', 'monitoring-profiles', props.tenantId],
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.monitoringProfilesPicker, { tenantId: props.tenantId }),
        GET_ROUTES.monitoringProfilesPicker.schema,
      ),
    enabled: open,
  });
  const mutation = useRegisterAccount(props.campaignId);
  function resetAndClose(): void {
    setOpen(false);
    setForm(EMPTY_FORM);
    mutation.reset();
  }
  const failure = mutation.error?.failure ?? null;
  const accountFieldError =
    fieldErrorOf(failure, 'handle') !== null ? aat('error.badHandle') : null;
  const bannerFailure = accountFieldError === null ? failure : null;
  function submit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const parsed = parseAccount(form.account);
    if (parsed === null) {
      setAccountMissing(true);
      return;
    }
    setAccountMissing(false);
    const alignment = alignments.find((option) => option.id === form.alignment);
    if (alignment === undefined) {
      setAlignmentMissing(true);
      return;
    }
    setAlignmentMissing(false);
    if (form.profileId === null) {
      setProfileMissing(true);
      return;
    }
    setProfileMissing(false);
    return mutation
      .mutateAsync({
        platform: parsed.platform,
        handle: parsed.handle,
        alignment: alignment.key,
        alignment_value_id: alignment.valueId,
        basis: DEFAULT_ACCOUNT_BASIS,
        value_ids: form.tags.map((pair) => pair.valueId),
        influence_scope: form.reach.scope,
        places:
          form.reach.places.length > 0 ? form.reach.places.map((place) => place.id) : undefined,
        profile_id: form.profileId,
      })
      .then(async (row) => {
        // `ALL` is already the campaign's default for Adversária (`tag_values.analysis_scope`):
        // only the narrower pick needs a follow-up write.
        if (alignment.key === 'ADVERSARY' && form.scope === 'RELATED') {
          await client.putParsed(
            `/campaigns/${props.campaignId}/tracked-accounts/${row.id}/analysis-scope`,
            { analysis_scope: 'RELATED' },
            analysisScopeStateSchema,
          );
        }
        return row;
      });
  }
  if (!props.canWrite) return null;
  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => (next ? setOpen(next) : resetAndClose())}
      trigger={controlled ? undefined : <Button type="button">{aat('trigger')}</Button>}
      title={aat('title')}
      submitLabel={aat('submit')}
      cancelLabel={aat('cancel')}
      pending={mutation.isPending}
      failure={null}
      onSubmit={submit}
      size="md"
      footerStart={<CostPreview campaignId={props.campaignId} profileId={form.profileId} />}
    >
      {bannerFailure !== null && (
        <Alert variant="destructive">
          <AlertTitle>{refusalCopy(bannerFailure)}</AlertTitle>
        </Alert>
      )}
      <Field data-invalid={accountFieldError !== null || accountMissing || undefined}>
        <FieldLabel htmlFor={accountId}>{aat('account.label')}</FieldLabel>
        <AccountUrlField
          id={accountId}
          placeholder={aat('account.placeholder')}
          value={form.account}
          onChange={(value) => setForm((f) => ({ ...f, account: value }))}
          {...fieldControlProps(failure, 'handle', `${accountId}-error`)}
        />
        <FieldError id={`${accountId}-error`}>
          {accountFieldError ?? (accountMissing ? aat('account.required') : null)}
        </FieldError>
      </Field>
      <FieldSet>
        <FieldLegend variant="label" className="flex w-full items-center justify-between gap-2">
          <span>{aat('collection.label')}</span>
          <a
            href={cataloguePath(props.tenantId)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-normal text-muted-foreground hover:text-foreground"
          >
            {aat('collection.profilesLink')}
            <ExternalLink aria-hidden className="size-3.5" />
          </a>
        </FieldLegend>
        <Field data-invalid={profileMissing || undefined}>
          <Select
            value={form.profileId ?? ''}
            onValueChange={(value) => {
              setProfileMissing(false);
              setForm((f) => ({ ...f, profileId: value }));
            }}
          >
            <SelectTrigger id={profileId} aria-label={aat('collection.profile')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(profiles.data?.items ?? []).map((profile) => (
                <SelectItem key={profile.id} value={profile.id}>
                  {profile.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{profileMissing ? aat('collection.profile.required') : null}</FieldError>
        </Field>
        <CollectionChips
          media={
            form.profileId === null
              ? null
              : (profiles.data?.items.find((profile) => profile.id === form.profileId)?.media ?? [])
          }
        />
      </FieldSet>
      <ReachField
        tenantId={props.tenantId}
        value={form.reach}
        onChange={(reach) => setForm((f) => ({ ...f, reach }))}
      />
      <FieldSet>
        <FieldLegend variant="label">{aat('alignment.label')}</FieldLegend>
        <ToggleGroup
          type="single"
          aria-label={aat('alignment.label')}
          aria-required="true"
          value={form.alignment ?? ''}
          onValueChange={(value) => {
            setAlignmentMissing(false);
            setForm((f) => ({ ...f, alignment: value === '' ? null : value }));
          }}
          className="flex-wrap"
        >
          {alignments.map((alignment) => (
            <ToggleGroupItem
              key={alignment.id}
              value={alignment.id}
              className={SEGMENTED_ITEM_CLASSNAME}
            >
              <TagGlyph name={alignment.icon} />
              {alignment.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <FieldError>{alignmentMissing ? aat('alignment.required') : null}</FieldError>
      </FieldSet>
      {alignments.find((option) => option.id === form.alignment)?.key === 'ADVERSARY' && (
        <FieldSet>
          <FieldLegend variant="label">{aat('scope.label')}</FieldLegend>
          <Select
            value={form.scope}
            onValueChange={(value) => setForm((f) => ({ ...f, scope: value as AnalysisScope }))}
          >
            <SelectTrigger aria-label={aat('scope.label')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{aat('scope.all')}</SelectItem>
              <SelectItem value="RELATED">{aat('scope.related')}</SelectItem>
            </SelectContent>
          </Select>
          {form.scope === 'ALL' && (
            <p className="text-xs text-muted-foreground">{aat('scope.all.hint')}</p>
          )}
        </FieldSet>
      )}
      <TagField
        campaignId={props.campaignId}
        value={form.tags}
        onChange={(tags) => setForm((f) => ({ ...f, tags }))}
      />
    </FormDialog>
  );
}
